// Medications list page — /patients/:id/medications

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { AddMedicationModal } from "../components/AddMedicationModal.js";
import { MedTable, type MedicationsResponse } from "../components/MedTable.js";

export function MedicationsPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const [showAdd, setShowAdd] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery<MedicationsResponse>({
    queryKey: ["medications", id],
    queryFn: () => apiFetch<MedicationsResponse>(`/patients/${id}/medications`),
    enabled: !!id,
  });

  const items = data?.data ?? [];

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={`/patients/${id}`}
              className="text-sm text-navy-700 hover:text-navy-900 transition-colors"
            >
              ← Patient
            </Link>
            <h1 className="text-xl font-semibold text-slate-900">
              Medications{" "}
              <span className="text-sm font-normal text-slate-400 font-mono">{`P-${id.slice(-5).toLowerCase()}`}</span>
            </h1>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors"
          >
            + Add Medication
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          {isLoading && <p className="py-8 text-center text-sm text-slate-400" aria-busy="true">Loading…</p>}
          {isError && <p role="alert" className="py-4 text-center text-sm text-red-600">Failed to load medications.</p>}
          {!isLoading && !isError && <MedTable items={items} patientId={id} />}
        </div>
      </div>
      {showAdd && (
        <AddMedicationModal
          patientId={id}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            void qc.invalidateQueries({ queryKey: ["medications", id] });
          }}
        />
      )}
    </Shell>
  );
}
